// ** Modules
import { useEffect, useCallback, useRef } from 'react';

type UseLightboxNavOptions = {
	open: boolean;
	itemCount: number;
	activeIndex: number;
	onClose: () => void;
	onPrev: () => void;
	onNext: () => void;
};

export const useLightboxNav = ({ open, itemCount, activeIndex, onClose, onPrev, onNext }: UseLightboxNavOptions) => {
	const hasPrev = activeIndex > 0;
	const hasNext = activeIndex < itemCount - 1;
	const touchStart = useRef<number | null>(null);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		touchStart.current = e.touches[0].clientX;
	}, []);

	const handleTouchEnd = useCallback(
		(e: React.TouchEvent) => {
			if (touchStart.current === null) return;
			const diff = touchStart.current - e.changedTouches[0].clientX;
			const threshold = 50;
			if (diff > threshold && hasNext) onNext();
			if (diff < -threshold && hasPrev) onPrev();
			touchStart.current = null;
		},
		[hasPrev, hasNext, onPrev, onNext]
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
			if (e.key === 'ArrowLeft' && hasPrev) onPrev();
			if (e.key === 'ArrowRight' && hasNext) onNext();
		},
		[onClose, onPrev, onNext, hasPrev, hasNext]
	);

	useEffect(() => {
		if (!open) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, handleKeyDown]);

	return {
		hasPrev,
		hasNext,
		handleTouchStart,
		handleTouchEnd
	};
};
